import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { CATEGORIES } from '@/mocks/categories';

export default function Navbar() {
  const { t, i18n } = useTranslation('common');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();

  const currentLang = location.pathname.split('/')[1] || 'en';

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const switchLanguage = (lang: string) => {
    const pathWithoutLang = location.pathname.replace(/^\/(en|pt|de)/, '') || '/';
    const newPath = `/${lang}${pathWithoutLang === '/' ? '' : pathWithoutLang}`;
    i18n.changeLanguage(lang);
    navigate(newPath);
  };

  const homeHref = `/${currentLang}`;
  const guideHref = `/${currentLang}/articles/cssbuy-spreadsheet-guide`;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b-2 border-background-200">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <a href={homeHref} className="flex items-center gap-2 text-foreground-900 font-bold text-xl md:text-2xl font-heading tracking-tight">
            <span className="text-primary-500">CSSBuy</span>
            <span>Spreadsheet</span>
          </a>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1">
            <a href={homeHref} className="px-4 py-2 text-sm font-semibold text-foreground-900 hover:text-primary-500 rounded-lg transition-colors whitespace-nowrap">
              {t('nav.home')}
            </a>

            <a href={`/${currentLang}/products`} className="px-4 py-2 text-sm font-semibold text-foreground-900 hover:text-primary-500 rounded-lg transition-colors whitespace-nowrap">
              {t('nav.products')}
            </a>

            <a href={guideHref} className="px-4 py-2 text-sm font-semibold text-foreground-900 hover:text-primary-500 rounded-lg transition-colors whitespace-nowrap">
              {t('nav.shoppingGuide')}
            </a>

            {/* Categories Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-foreground-900 hover:text-primary-500 rounded-lg transition-colors whitespace-nowrap"
              >
                {t('dropdown.label')}
                <i className={`ri-arrow-down-s-line transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {dropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-xl border border-background-200 py-2 z-50 max-h-[70vh] overflow-y-auto">
                  {CATEGORIES.map((cat) => (
                    <a
                      key={cat.key}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2 text-sm text-foreground-800 hover:bg-primary-50 hover:text-primary-600 transition-colors"
                    >
                      <img src={cat.img} alt="" className="w-8 h-8 rounded-md object-cover" />
                      <span>{t(cat.key)}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="px-4 py-2 text-sm font-semibold text-foreground-900 hover:text-primary-500 rounded-lg transition-colors whitespace-nowrap">
              {t('cta.cssbuySpreadsheet')}
            </a>
            <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="px-4 py-2 text-sm font-semibold bg-primary-500 text-background-50 rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap">
              {t('cta.buyNow')}
            </a>

            {/* Language Switcher */}
            <div className="flex items-center gap-0.5 ml-4 pl-4 border-l border-background-200">
              {(['en', 'pt', 'de'] as const).map((lang) => (
                <button
                  key={lang}
                  onClick={() => switchLanguage(lang)}
                  className={`px-2 py-1 text-xs font-semibold rounded transition-colors ${
                    currentLang === lang
                      ? 'text-primary-600 bg-primary-50'
                      : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  {t(`lang.${lang}`)}
                </button>
              ))}
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 text-foreground-900 hover:text-primary-500 transition-colors"
            aria-label="Menu"
          >
            <i className={`ri-${mobileMenuOpen ? 'close' : 'menu'}-line text-2xl`} />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-background-200 px-4 py-4 max-h-[80vh] overflow-y-auto">
          {/* Primary actions */}
          <div className="grid grid-cols-2 gap-2 mb-4">
            <a href={homeHref} className="flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm font-semibold bg-background-100 text-foreground-900 rounded-lg whitespace-nowrap">
              <i className="ri-home-4-line" />
              {t('nav.home')}
            </a>
            <a href={`/${currentLang}/products`} className="flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm font-semibold bg-background-100 text-foreground-900 rounded-lg whitespace-nowrap">
              <i className="ri-shopping-bag-line" />
              {t('nav.products')}
            </a>
          </div>
          <div className="grid grid-cols-2 gap-2 mb-4">
            <a href={guideHref} className="flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm font-semibold bg-primary-500 text-background-50 rounded-lg whitespace-nowrap">
              <i className="ri-book-2-line" />
              {t('nav.shoppingGuide')}
            </a>
          </div>

          <div className="text-sm font-semibold text-foreground-500 mb-2 mt-4">{t('dropdown.label')}</div>
          {/* Category grid */}
          <div className="grid grid-cols-2 gap-2">
            {CATEGORIES.map((cat) => (
              <a
                key={cat.key}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center gap-2 p-2 bg-background-50 rounded-lg border border-background-200 hover:border-primary-300 transition-colors"
              >
                <img src={cat.img} alt="" className="w-9 h-9 rounded-md object-cover flex-shrink-0" />
                <span className="text-xs font-medium text-foreground-800 leading-tight">{t(cat.key)}</span>
              </a>
            ))}
          </div>

          <div className="border-t border-background-200 mt-4 pt-3 space-y-2">
            <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="block py-2 text-sm font-semibold text-foreground-900">
              {t('cta.cssbuySpreadsheet')}
            </a>
            <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="block py-2 text-sm font-semibold text-foreground-900">
              {t('cta.buyNow')}
            </a>
          </div>

          <div className="border-t border-background-200 mt-3 pt-3 flex gap-2">
            {(['en', 'pt', 'de'] as const).map((lang) => (
              <button
                key={lang}
                onClick={() => switchLanguage(lang)}
                className={`px-3 py-1.5 text-sm font-semibold rounded ${
                  currentLang === lang
                    ? 'text-primary-600 bg-primary-50'
                    : 'text-foreground-500'
                }`}
              >
                {t(`lang.${lang}`)}
              </button>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
}